import React, { FC, useEffect, useMemo } from 'react';
import ApprovalPostCommentsThread from './ApprovalPostCommentsThread';
import ApprovalCommentInput from './ApprovalCommentInput';
import ApprovalActionButtons from '../ApprovalActionButtons';
import { ApprovalThreadType } from '../../types';
import { usePostCommunicationContext } from 'app/modules/posts/modules/post/components/postCommunication/PostCommunicationProvider';
import { useCommentsSummary } from 'app/modules/posts/modules/post/components/postCommunication/comments/hooks/useCommentsSummary';
import { useUpdateApprovalPostStatus } from 'app/modules/posts/modules/detail/hooks/useUpdateApprovalPostStatus';
import { useTranslation } from 'react-i18next';
import { ApprovedPost } from '../../context/ApprovedPostsContext';
import { usePostsContainer } from 'components/posts/context';
import { useLocation } from 'react-router-dom';
import routes from 'constants/routes';

type Props = {
  thread: ApprovalThreadType;
  onApprovalPageSuccess?: () => void;
};

const ApprovalCommunicationContent: FC<Props> = ({
  thread,
  onApprovalPageSuccess,
}) => {
  const {
    setReadyToTrackVisibility,
    postId,
    resetCommentAction,
    initReworkCommentAction,
  } = usePostCommunicationContext();
  const { t } = useTranslation();
  const { data: commentsSummary } = useCommentsSummary();
  const updateApprovalStatus = useUpdateApprovalPostStatus({
    postId,
    commentThread: thread,
  });
  const { posts } = usePostsContainer();
  const location = useLocation();
  const isOnApprovalsPage = location.pathname.startsWith(
    routes.POSTS_APPROVALS,
  );

  const approvalPost = useMemo(() => {
    return posts.allPosts.find((p) => p.id === postId);
  }, [posts.allPosts, postId]);

  const approvedPostData: ApprovedPost | undefined = useMemo(() => {
    if (!approvalPost || !isOnApprovalsPage) return undefined;
    return {
      id: approvalPost.id,
      title: approvalPost.text || '',
      thumbnail: approvalPost.preview_image?.src ?? null,
      previousStatus: approvalPost.status,
      thread,
      pageId: approvalPost.page.id,
      pageType: approvalPost.page.type,
    };
  }, [approvalPost, thread, isOnApprovalsPage]);

  useEffect(() => {
    setReadyToTrackVisibility(true);
    return () => {
      setReadyToTrackVisibility(false);
    };
  }, [setReadyToTrackVisibility]);

  const showApprovalButtons =
    commentsSummary && !commentsSummary.pendingApprovalRequest;

  const handleApprove = () => {
    resetCommentAction();
    updateApprovalStatus.updateThroughComment.mutate(
      {
        thread,
        action: `${thread}_approval`,
        text: t('commentsWorkflowMessages.approve'),
        approvedPostData,
      },
      {
        onSuccess: () => {
          onApprovalPageSuccess?.();
        },
      },
    );
  };

  const handleRework = () => {
    initReworkCommentAction(thread);
  };

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      <div className="tw-flex-1 tw-overflow-y-auto tw-px-3">
        <ApprovalPostCommentsThread className="tw-h-full" thread={thread} />
      </div>
      <ApprovalCommentInput thread={thread} />
      {showApprovalButtons && (
        <ApprovalActionButtons
          thread={thread}
          isLoading={updateApprovalStatus.updateThroughComment.isLoading}
          onApprove={handleApprove}
          onRework={handleRework}
        />
      )}
    </div>
  );
};

export default ApprovalCommunicationContent;
